/**
 * HLS playback helpers that align **wall-clock time** (Unix-style ms) with the
 * **media timeline** (`<video>.currentTime`, seconds) using `#EXT-X-PROGRAM-DATE-TIME`
 * (PDT) and per-fragment durations from the manifest as parsed by hls.js.
 *
 * Why this exists: cumulative `EXTINF` lengths on the wire can disagree with the
 * muxed timeline the decoder actually plays. A naive `baseTs + currentTime * 1000`
 * then drifts from the playlist’s declared program times. These functions use the
 * same fragment model hls.js uses so seeks and UI timestamps stay consistent with PDT.
 */
import type Hls from 'hls.js';
import type Player from 'video.js/dist/types/player';

/**
 * Returns the hls.js instance that {@link setupHlsJs} stores on the Video.js player
 * (`player.hls`), or `null` if the player is not using that integration (e.g. native
 * HLS, no tech yet, or disposal).
 *
 * All Video.js–specific helpers in this module use this to decide whether PDT-based
 * mapping is available; when it is missing, callers fall back to linear `baseTs` math
 * or a precomputed seek position.
 */
export function getHlsFromVideoJsPlayer(player: Player): Hls | null {
  return (player as unknown as { hls?: Hls }).hls ?? null;
}

type PdtFragment = {
  start: number;
  duration: number;
  programDateTime: number | null;
};

/**
 * Resolves the fragment list for the **currently selected** HLS level (`currentLevel`,
 * or `firstLevel` when none is selected yet). Each entry mirrors hls.js fragment
 * fields used here: mux start (`start`), `duration`, and optional `programDateTime`
 * (ms since Unix epoch) when the playlist carried PDT for that segment.
 */
function mainLevelFragments(hls: Hls): PdtFragment[] | null {
  const idx = hls.currentLevel >= 0 ? hls.currentLevel : (hls.firstLevel ?? 0);
  const level = hls.levels[idx];
  const raw = level?.details?.fragments;

  if (!raw?.length) {
    return null;
  }

  return raw as PdtFragment[];
}

/**
 * Walks PDT-backed fragments on the active level; returns the first non-null result from
 * `tryMap`, or `null` if there are no fragments or every fragment yields null.
 */
function mapFirstMatchingPdtFragment<R>(
  hls: Hls,
  tryMap: (f: PdtFragment, pdtMs: number) => R | null,
): R | null {
  const frags = mainLevelFragments(hls);
  if (!frags) {
    return null;
  }
  for (const f of frags) {
    const pdtMs = f.programDateTime;
    if (pdtMs == null) {
      continue;
    }
    const mapped = tryMap(f, pdtMs);
    if (mapped !== null) {
      return mapped;
    }
  }
  return null;
}

function mapWithVideoJsHls<R>(player: Player, fn: (hls: Hls) => R | null): R | null {
  const hls = getHlsFromVideoJsPlayer(player);
  return hls ? fn(hls) : null;
}

/**
 * **Wall clock → media seconds** (inverse of {@link wallMsForMediaTimelineSeconds}).
 *
 * Given an absolute wall time in milliseconds, finds which loaded fragment’s PDT
 * window contains that instant and returns the corresponding position on the **mux**
 * media timeline in seconds (`frag.start` plus the offset inside the fragment).
 *
 * Algorithm: walk fragments in level order; for each fragment with non-null PDT,
 * treat `[programDateTime, programDateTime + duration * 1000)` as half-open wall
 * coverage; the first match wins.
 *
 * Typical use: converting a UI or logger “seek to this wall timestamp” into the
 * `currentTime` value Video.js should use when the stream is tagged with PDT.
 *
 * @param hls - Active hls.js instance (same level/fragments as playback).
 * @param wallMs - Absolute wall time in ms (e.g. epoch ms from the app).
 * @returns Media timeline seconds, or `null` if there is no fragment list, no PDT on
 *   fragments, or `wallMs` lies outside every PDT interval (gaps, future window, etc.).
 *   Callers usually fall back to `(wallMs - baseTs) / 1000` or a caller-supplied seek time.
 */
export function mediaSecondsForProgramDateWallMs(hls: Hls, wallMs: number): number | null {
  return mapFirstMatchingPdtFragment(hls, (f, pdtMs) => {
    const endWallMs = pdtMs + f.duration * 1000;
    if (wallMs >= pdtMs && wallMs < endWallMs) {
      return f.start + (wallMs - pdtMs) / 1000;
    }
    return null;
  });
}

/**
 * **Media seconds → wall clock ms** (inverse of {@link mediaSecondsForProgramDateWallMs}).
 *
 * Interprets `mediaTimeSec` as the decoded timeline position (same domain as
 * `player.currentTime()` with hls.js). Locates the fragment whose mux range
 * `[start, start + duration)` contains that time, then linearly maps the offset inside
 * the fragment to wall ms using that fragment’s PDT as anchor. The result is rounded
 * to the nearest integer millisecond.
 *
 * Use this for `timeupdate`-style emission so “now” in the UI matches manifest program
 * time rather than only `baseTs + currentTime * 1000`, which can diverge when segment
 * list metadata and the real mux timeline disagree.
 *
 * @param hls - Active hls.js instance.
 * @param mediaTimeSec - Playhead position on the media timeline in seconds.
 * @returns Wall time in ms for the playhead within a PDT-backed fragment, or `null` if
 *   fragments are missing, lack PDT, or `mediaTimeSec` is outside any such fragment.
 */
export function wallMsForMediaTimelineSeconds(hls: Hls, mediaTimeSec: number): number | null {
  return mapFirstMatchingPdtFragment(hls, (f, pdtMs) => {
    const fragEnd = f.start + f.duration;
    if (mediaTimeSec + 1e-9 >= f.start && mediaTimeSec < fragEnd) {
      return Math.round(pdtMs + (mediaTimeSec - f.start) * 1000);
    }
    return null;
  });
}

/**
 * Computes the **wall timestamp (ms)** to report for the current playhead, for adapters
 * that emit something like `TS_UPDATE` on each `timeupdate`.
 *
 * When `getHlsFromVideoJsPlayer(player)` returns an instance, delegates to
 * {@link wallMsForMediaTimelineSeconds} so the value tracks PDT on the active level.
 * When there is no hls.js handle or PDT cannot resolve the position, returns
 * `Math.round(baseTs + mediaTimeSec * 1000)` — i.e. a linear session clock from a known
 * stream start `baseTs` plus elapsed media seconds.
 *
 * @param player - Video.js player (may or may not have `.hls`).
 * @param mediaTimeSec - Same basis as `player.currentTime()` (seconds).
 * @param baseTs - Wall ms at media timeline zero for the linear fallback.
 * @returns Always a number: PDT-derived wall ms when possible, otherwise linear fallback.
 */
export function wallTsForVideoJsPlayhead(
  player: Player,
  mediaTimeSec: number,
  baseTs: number,
): number {
  const pdtWallMs = mapWithVideoJsHls(player, (hls) =>
    wallMsForMediaTimelineSeconds(hls, mediaTimeSec),
  );
  return pdtWallMs ?? Math.round(baseTs + mediaTimeSec * 1000);
}

/**
 * Computes **media timeline seconds** to pass to `player.currentTime(...)` when the user
 * or controller requests a position identified by **wall time** (`wallMs`).
 *
 * With hls.js attached, uses {@link mediaSecondsForProgramDateWallMs} so the seek lands
 * on the segment that the manifest associates with that program date. That pairs with
 * {@link wallTsForVideoJsPlayhead}: the same wall instant maps consistently in both
 * directions when PDT data exists.
 *
 * Without hls.js or when PDT cannot resolve `wallMs`, returns `fallbackSeekSeconds`.
 * Callers typically pass the seek position already computed in media seconds (e.g. from
 * a scrub bar that was driven by linear time), so behavior matches the pre-PDT path.
 *
 * @param player - Video.js player.
 * @param wallMs - Target wall time in ms (e.g. same domain as values from {@link wallTsForVideoJsPlayhead}).
 * @param fallbackSeekSeconds - Media seconds to use when PDT mapping is unavailable.
 * @returns Seconds on the media timeline to assign as `currentTime`.
 */
export function seekSecondsFromVideoJsWallTs(
  player: Player,
  wallMs: number,
  fallbackSeekSeconds: number,
): number {
  const pdtMappedSeekSeconds = mapWithVideoJsHls(player, (hls) =>
    mediaSecondsForProgramDateWallMs(hls, wallMs),
  );
  return pdtMappedSeekSeconds ?? fallbackSeekSeconds;
}
